import { MaterialSymbol } from '@florencelabs/icons';
import {
  ModalCloseButton,
  DialogTrigger as MenuDialogTrigger,
  Modal,
  Dialog,
  ModalProps,
} from '@florencelabs/modal-2';
import clsx from 'clsx';
import React, {
  ReactNode,
  ReactElement,
  ComponentType,
  PropsWithChildren,
} from 'react';
import {
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuProps as AriaMenuProps,
  MenuItemProps,
  MenuTrigger,
  Popover as AriaPopover,
  PopoverProps as AriaPopoverProps,
  composeRenderProps,
  Pressable as MenuPressable,
  MenuSection as AriaMenuSection,
  Header,
  type MenuSectionProps,
  type Key as MenuKey,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { tv } from 'tailwind-variants';
import { cn } from '@provider-directory/functions/cn';
import { useIsDesktop } from '@provider-directory/hooks/useBreakpoints';

export interface PopoverProps extends Omit<AriaPopoverProps, 'children'> {
  children: React.ReactNode;
}

const styles = tv({
  base: 'bg-white shadow-md rounded-lg bg-clip-padding border-white',
  variants: {
    isEntering: {
      true: 'animate-in fade-in placement-bottom:slide-in-from-top-1 placement-top:slide-in-from-bottom-1 placement-left:slide-in-from-right-1 placement-right:slide-in-from-left-1 ease-out duration-200',
    },
    isExiting: {
      true: 'animate-out fade-out placement-bottom:slide-out-to-top-1 placement-top:slide-out-to-bottom-1 placement-left:slide-out-to-right-1 placement-right:slide-out-to-left-1 ease-in duration-150',
    },
  },
});

const Popover = ({ children, ...props }: PopoverProps): ReactElement => {
  const offset = 8;
  return (
    <AriaPopover
      offset={offset}
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        styles({ ...renderProps, className }),
      )}
    >
      {children}
    </AriaPopover>
  );
};

interface MenuPopoverProps<T> extends AriaMenuProps<T> {
  placement?: PopoverProps['placement'];
}

export const MenuPopover = <T extends object>({
  placement,
  onAction,
  ...props
}: MenuPopoverProps<T>): ReactElement => {
  return (
    <Popover placement={placement} className={'min-w-[200px]'}>
      <AriaMenu
        onAction={onAction}
        {...props}
        disallowEmptySelection={true}
        className={
          'flex max-h-[inherit] flex-col overflow-auto outline outline-0 [clip-path:inset(0_0_0_0_round_.75rem)]'
        }
      />
    </Popover>
  );
};

interface ModalMenuHeaderProps {
  heading: string;
  onPressClose: () => void;
}

const MenuModalHeader = ({
  heading,
  onPressClose,
}: ModalMenuHeaderProps): ReactElement => {
  const { t } = useTranslation();

  return (
    <div
      className={
        'border-b border-black border-opacity-10 px-6 pb-6 tablet:px-16 desktop:px-6'
      }
    >
      <div className={'flex flex-row items-center justify-between'}>
        <div className={'text-body-medium-medium'}>{heading}</div>
        <ModalCloseButton
          slot={null}
          onPress={onPressClose}
          className={'relative inline-block h-6 w-6'}
          ariaLabel={t('aria.closeMenu')}
        />
      </div>
    </div>
  );
};

interface MenuModalProps<T>
  extends Pick<ModalMenuHeaderProps, 'heading'>,
    AriaMenuProps<T> {
  isOpen: boolean;
  onOpenChange: () => void;
  shouldCloseOnInteractOutside?: (element: Element) => boolean;
  testIdPrefix: string;
}

export const MenuModal = <T extends object>({
  heading,
  isOpen,
  onOpenChange,
  shouldCloseOnInteractOutside,
  testIdPrefix,
  ...props
}: MenuModalProps<T>): ReactElement => {
  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isKeyboardDismissDisabled={false}
      isDismissable={true}
      variant={'bottomDrawer'}
      className={'h-fit rounded-t-lg'}
      shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
    >
      <Dialog
        aria-label={heading}
        className={'px-0 pt-6'}
        data-testid={`${testIdPrefix}-selector-modal`}
      >
        <MenuModalHeader heading={heading} onPressClose={onOpenChange} />
        <AriaMenu
          data-testid={`${testIdPrefix}-selector-options`}
          {...props}
          disallowEmptySelection={true}
          className={
            'flex max-h-[inherit] flex-col overflow-auto outline outline-0'
          }
        />
      </Dialog>
    </Modal>
  );
};

export const MenuSection = (props: MenuSectionProps<object>): ReactElement => {
  return <AriaMenuSection {...props} />;
};

export const MenuHeader = ({ label }: { label: string }): ReactElement => {
  return (
    <Header className={'pb-2 pt-4'} data-testid={'menu-header'}>
      <span
        className={
          'select-none px-6 text-body-medium-semibold tablet:px-16 desktop:px-6'
        }
      >
        {label}
      </span>
    </Header>
  );
};

export const MenuItem = (props: MenuItemProps): ReactElement => {
  const textValue =
    props.textValue ||
    (typeof props.children === 'string' ? props.children : undefined);
  return (
    <AriaMenuItem
      textValue={textValue}
      {...props}
      className={clsx(
        'flex cursor-pointer py-3 text-body-medium outline outline-0 first:pt-5 last:pb-5 hover:bg-primary-50 first:hover:rounded-t-lg last:hover:rounded-b-lg',
        props.className,
      )}
    >
      {composeRenderProps(
        props.children,
        (children, { selectionMode, isSelected, isDisabled }) => (
          <>
            <span
              className={clsx(
                'flex flex-1 items-center justify-between gap-2 truncate px-6 text-body-medium-semibold tablet:px-16 desktop:px-6',
                {
                  'text-primary-color': selectionMode !== 'none' && isSelected,
                  'cursor-default opacity-50': isDisabled,
                },
              )}
            >
              {children}
              {selectionMode !== 'none' && (
                <span className={'flex items-center'}>
                  {isSelected && (
                    <MaterialSymbol
                      name={'check'}
                      className={'text-primary-color'}
                    />
                  )}
                </span>
              )}
            </span>
          </>
        ),
      )}
    </AriaMenuItem>
  );
};

export const MenuSectionItem = ({
  className,
  ...props
}: MenuItemProps): ReactElement => {
  return <MenuItem {...props} className={cn('pl-4', className)} />;
};

type PickedModalProps = Pick<ModalProps, 'shouldCloseOnInteractOutside'>;

export interface MenuProps<T>
  extends AriaMenuProps<T>,
    PickedModalProps,
    PropsWithChildren {
  children: ReactNode;
  TriggerComponent: ComponentType<any>;
  triggerComponentProps?: any;
  isOpen: boolean;
  onOpenChange: () => void;
  placement?: PopoverProps['placement'];
  onAction?: (key: MenuKey) => void;
  heading: string;
  testIdPrefix: string;
}

export const Menu = <T extends object>({
  children,
  TriggerComponent,
  triggerComponentProps,
  isOpen,
  onOpenChange,
  placement,
  onAction,
  heading,
  shouldCloseOnInteractOutside, // from PickedModalProps
  testIdPrefix = 'component',
  ...props
}: MenuProps<T>): ReactElement => {
  const isDesktop = useIsDesktop();
  const triggerProps = { isOpen, onOpenChange };

  return (
    <div data-testid={`${testIdPrefix}-selector`}>
      {isDesktop ? (
        <MenuTrigger {...triggerProps}>
          <TriggerComponent {...triggerComponentProps} />
          <MenuPopover
            data-testid={`${testIdPrefix}-selector-options`}
            placement={placement}
            onAction={onAction}
            {...props}
          >
            {children}
          </MenuPopover>
        </MenuTrigger>
      ) : (
        <MenuDialogTrigger {...triggerProps}>
          <TriggerComponent {...triggerComponentProps} />
          <MenuModal
            heading={heading}
            onAction={onAction}
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
            testIdPrefix={testIdPrefix}
            {...props}
          >
            {children}
          </MenuModal>
        </MenuDialogTrigger>
      )}
    </div>
  );
};

export { MenuTrigger, MenuPressable, MenuKey, MenuDialogTrigger };
